import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import Navigation from '../components/Navigation';

interface Settings {
  slotDurationMinutes: number;
  slotIntervalMinutes: number;
  openingHour: number;
  closingHour: number;
  availableKarts: number;
}

interface Booking {
  id: string;
  date: Timestamp;
  startTime: string;
  endTime: string;
  status: string;
  userName: string;
  userEmail?: string;
  racerId?: string;
  itemNumber?: number;
  createdAt?: Timestamp;
  cancelledAt?: Timestamp;
  cancelledBy?: string;
  bookingType: 'gokart' | 'simulator' | 'vr' | 'playhouse';
  motherName?: string;
  birthDate?: string;
  address?: string;
  phoneNumber?: string;
  idNumber?: string;
  racerDetails?: {
    fullName?: string;
    motherName?: string;
    idCardNumber?: string;
    address?: string;
    socialSecurityNumber?: string;
    email?: string;
    phoneNumber?: string;
  };
}

// Csoportosított foglalás típus
interface GroupedBooking {
  date: Timestamp;
  startTime: string;
  endTime: string;
  bookings: Booking[];
}

const statusTranslations: { [key: string]: string } = {
  'confirmed': 'Megerősítve',
  'pending': 'Függőben',
  'completed': 'Teljesítve',
  'cancelled': 'Lemondva'
};

// Add this helper function near the top of the file, after the interfaces
const isMoreThan24HoursBefore = (bookingDate: Timestamp) => {
  const now = new Date();
  const bookingTime = bookingDate.toDate();
  const diffInHours = (bookingTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  return diffInHours > 24;
};

const Dashboard: React.FC = () => {
  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [userBookings, setUserBookings] = useState<Booking[]>([]);
  const [groupedBookings, setGroupedBookings] = useState<GroupedBooking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupedBooking | null>(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
  const [showAllPastBookings, setShowAllPastBookings] = useState(false);
  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'gokart' | 'simulator' | 'vr' | 'playhouse'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all');
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pastSortOrder, setPastSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
      fetchPendingBookingsCount();
    }

    if (currentUser) {
      fetchUserBookings();
    }
  }, [isAdmin, currentUser]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'bookings'));

      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data() as Settings;
        setSettings(settingsData);
      }
    } catch (error) {
      console.error('Hiba a beállítások lekérése során:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKartCountChange = async (newCount: number) => {
    if (!settings) return;

    try {
      setSaving(true);

      // Frissítjük a beállításokat
      const updatedSettings = {
        ...settings,
        availableKarts: newCount
      };

      await updateDoc(doc(db, 'settings', 'bookings'), updatedSettings);
      setSettings(updatedSettings);

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a gokartok számának módosítása során:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Hiba a kijelentkezés során:', error);
    }
  };

  // Foglalások csoportosítása dátum és időpont szerint
  const groupBookingsByDateTime = (bookings: Booking[]): GroupedBooking[] => {
    const groups: { [key: string]: GroupedBooking } = {};

    bookings.forEach(booking => {
      const dateStr = booking.date.toDate().toISOString().split('T')[0];
      const key = `${dateStr}_${booking.startTime}_${booking.endTime}`;

      if (!groups[key]) {
        groups[key] = {
          date: booking.date,
          startTime: booking.startTime,
          endTime: booking.endTime,
          bookings: []
        };
      }

      groups[key].bookings.push(booking);
    });

    return Object.values(groups).sort((a, b) => b.date.toMillis() - a.date.toMillis());
  };

  const fetchUserBookings = async () => {
    if (!currentUser) return;

    try {
      setBookingsLoading(true);

      // Most már csak egy lekérdezés a bookings kollekcióból
      const bookingsRef = collection(db, 'bookings');
      const bookingsQuery = query(
        bookingsRef,
        where('userId', '==', currentUser.uid),
        orderBy('date', 'desc')
      );

      const bookingsSnapshot = await getDocs(bookingsQuery);
      const allBookings = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];

      setUserBookings(allBookings);

      // Csoportosítjuk a foglalásokat dátum és időpont szerint
      const grouped = groupBookingsByDateTime(allBookings);
      setGroupedBookings(grouped);
    } catch (error) {
      console.error('Hiba a foglalások lekérése során:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const isUpcoming = (timestamp: Timestamp) => {
    const now = new Date();
    const bookingDate = timestamp.toDate();
    // Az időpontokat milliszekundumokra konvertáljuk a pontos összehasonlításhoz
    return bookingDate.getTime() > now.getTime();
  };

  const deleteBooking = async (bookingId: string) => {
    if (!currentUser) return;

    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingDoc = await getDoc(bookingRef);
      const bookingData = bookingDoc.data() as Booking;

      // Check if booking is within 24 hours
      if (!isMoreThan24HoursBefore(bookingData.date)) {
        alert('A foglalást már nem lehet lemondani, mert 24 órán belül van!');
        return;
      }

      await updateDoc(bookingRef, {
        status: 'cancelled',
        cancelledAt: Timestamp.now(),
        cancelledBy: currentUser.uid
      });

      // Frissítjük a foglalások listáját
      setUserBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.id === bookingId
            ? { ...booking, status: 'cancelled', cancelledAt: Timestamp.now() }
            : booking
        )
      );

      // Frissítjük a csoportosított foglalásokat
      setGroupedBookings(prevGroups =>
        prevGroups.map(group => ({
          ...group,
          bookings: group.bookings.map(booking =>
            booking.id === bookingId
              ? { ...booking, status: 'cancelled', cancelledAt: Timestamp.now() }
              : booking
          )
        }))
      );

      setDeleteSuccess(true);
      setDeleteError(null);

      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a foglalás törlése során:', error);
      setDeleteError('Nem sikerült törölni a foglalást. Kérjük, próbáld újra!');

      setTimeout(() => {
        setDeleteError(null);
      }, 3000);
    }
  };

  const deleteAllBookingsInGroup = (group: GroupedBooking) => {
    // Check if any booking is within 24 hours
    const isWithin24Hours = !isMoreThan24HoursBefore(group.date);

    if (isWithin24Hours) {
      alert('A foglalást már nem lehet lemondani, mert 24 órán belül van!');
      return;
    }

    if (window.confirm('Biztosan le szeretnéd mondani az összes függőben lévő foglalást ebben az időpontban?')) {
      group.bookings.forEach(booking => {
        if (booking.status === 'pending') {
          deleteBooking(booking.id);
        }
      });
      closeGroupDetails();
    }
  };

  const viewBookingDetails = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  const closeBookingDetails = () => {
    setShowBookingDetails(false);
    setSelectedBooking(null);
  };

  const viewGroupDetails = (group: GroupedBooking) => {
    setSelectedGroup(group);
    setShowGroupDetails(true);
  };

  const closeGroupDetails = () => {
    setShowGroupDetails(false);
    setSelectedGroup(null);
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Függőben lévő foglalások számának lekérdezése
  const fetchPendingBookingsCount = async () => {
    try {
      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('status', '==', 'pending')
      );

      const bookingsSnapshot = await getDocs(q);
      setPendingBookingsCount(bookingsSnapshot.size);
    } catch (error) {
      console.error('Hiba a függőben lévő foglalások számának lekérdezése során:', error);
    }
  };

  // Pagination helper functions
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterDate, filterType, filterStatus]);

  // Add this before the return statement
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  const getAvailableYears = () => {
    const years = new Set<string>();
    const currentYear = getCurrentYear();
    years.add(currentYear);

    groupedBookings.forEach(group => {
      const year = group.date.toDate().getFullYear().toString();
      years.add(year);
    });

    return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
  };

  // Calculate filtered bookings once
  const filteredBookings = groupedBookings.filter(group => {
    // Dátum szűrő
    if (filterDate) {
      const filterDateStr = filterDate.toISOString().split('T')[0];
      const groupDateStr = group.date.toDate().toISOString().split('T')[0];
      if (filterDateStr !== groupDateStr) return false;
    }

    // Év szűrő
    if (filterYear !== 'all') {
      const groupYear = group.date.toDate().getFullYear().toString();
      if (groupYear !== filterYear) return false;
    }

    // Hónap szűrő
    if (filterMonth !== 'all') {
      const groupMonth = `${String(group.date.toDate().getMonth() + 1).padStart(2, '0')}`;
      if (groupMonth !== filterMonth) return false;
    }

    // Típus szűrő
    if (filterType !== 'all') {
      if (!group.bookings.some(b => b.bookingType === filterType)) return false;
    }

    // Állapot szűrő
    if (filterStatus !== 'all') {
      if (!group.bookings.some(b => b.status === filterStatus)) return false;
    }

    return true;
  }).sort((a, b) => {
    // Először dátum szerint rendezünk
    const dateComparison = sortOrder === 'asc'
      ? a.date.toMillis() - b.date.toMillis()
      : b.date.toMillis() - a.date.toMillis();

    if (dateComparison !== 0) {
      return dateComparison;
    }

    // Ha a dátum megegyezik, akkor időpont szerint rendezünk
    const aTime = a.startTime.split(':').map(Number);
    const bTime = b.startTime.split(':').map(Number);

    // Óra összehasonlítása
    if (aTime[0] !== bTime[0]) {
      return sortOrder === 'asc' ? aTime[0] - bTime[0] : bTime[0] - aTime[0];
    }

    // Perc összehasonlítása
    return sortOrder === 'asc' ? aTime[1] - bTime[1] : bTime[1] - aTime[1];
  });

  // Add this function before the return statement
  const handleSortByDate = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handlePastSortByDate = () => {
    setPastSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Add filteredPastBookings calculation before the return statement
  const filteredPastBookings = groupedBookings.filter(group => {
    // Dátum szűrő
    if (filterDate) {
      const filterDateStr = filterDate.toISOString().split('T')[0];
      const groupDate = group.date.toDate();
      const groupDateStr = `${groupDate.getFullYear()}-${String(groupDate.getMonth() + 1).padStart(2, '0')}-${String(groupDate.getDate()).padStart(2, '0')}`;
      if (filterDateStr !== groupDateStr) return false;
    }

    // Típus szűrő
    if (filterType !== 'all') {
      if (!group.bookings.some(b => b.bookingType === filterType)) return false;
    }

    // Állapot szűrő
    if (filterStatus !== 'all') {
      if (!group.bookings.some(b => b.status === filterStatus)) return false;
    }

    return true;
  });

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-gray-900">Vezérlőpult</h1>
        </div>

        {isAdmin && settings && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
            <div className="bg-red-50 p-4 rounded-lg shadow-md border border-red-200">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium text-red-800">Gokartok kezelése</h2>
                  <p className="text-sm text-red-600 mt-1">
                    Jelenleg <span className="font-bold">{settings.availableKarts}</span> gokart érhető el a foglalásokhoz.
                  </p>
                </div>

                <div className="mt-4 md:mt-0 flex items-center">
                  <span className="text-sm font-medium text-gray-700 mr-2">Elérhető gokartok:</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleKartCountChange(Math.max(1, settings.availableKarts - 1))}
                      disabled={settings.availableKarts <= 1 || saving}
                      className={`px-3 py-1 rounded-md ${settings.availableKarts <= 1 || saving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                    >
                      -
                    </button>
                    <span className="px-3 py-1 bg-white border border-gray-300 rounded-md font-bold">
                      {settings.availableKarts}
                    </span>
                    <button
                      onClick={() => handleKartCountChange(Math.min(20, settings.availableKarts + 1))}
                      disabled={settings.availableKarts >= 20 || saving}
                      className={`px-3 py-1 rounded-md ${settings.availableKarts >= 20 || saving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {saveSuccess && (
                <div className="mt-2 p-2 bg-green-100 text-green-800 rounded-md text-sm">
                  A gokartok száma sikeresen módosítva!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Felhasználói foglalások szekció */}
        {currentUser && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <div className="bg-white overflow-hidden shadow-sm rounded-lg">
              <div className="p-6 bg-white border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Foglalásaim</h2>

                {deleteSuccess && (
                  <div className="mb-4 p-3 bg-green-100 text-green-800 rounded-md">
                    A foglalás sikeresen lemondva!
                  </div>
                )}

                {deleteError && (
                  <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
                    {deleteError}
                  </div>
                )}

                {bookingsLoading ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600">Foglalások betöltése...</p>
                  </div>
                ) : groupedBookings.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-600">Még nincs foglalásod.</p>
                    <button
                      onClick={() => navigate('/public-booking')}
                      className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                    >
                      Új foglalás
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Szűrők ide kerülnek, még a táblázat előtt */}
                    <div className="mt-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end">
                      <div className="flex-1 w-full sm:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dátum szerint</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value) : null;
                            setFilterDate(date);
                          }}
                          value={filterDate ? filterDate.toISOString().split('T')[0] : ''}
                        />
                      </div>
                      <div className="flex-1 w-full sm:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Év szerint</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={filterYear}
                          onChange={(e) => setFilterYear(e.target.value)}
                        >
                          <option value="all">Összes év</option>
                          {getAvailableYears().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1 w-full sm:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hónap szerint</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={filterMonth}
                          onChange={(e) => setFilterMonth(e.target.value)}
                        >
                          <option value="all">Összes hónap</option>
                          {Array.from({ length: 12 }, (_, i) => {
                            const month = new Date(2000, i);
                            return (
                              <option key={i} value={String(i + 1).padStart(2, '0')}>
                                {month.toLocaleString('hu-HU', { month: 'long' })}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="flex-1 w-full sm:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Típus szerint</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value as 'all' | 'gokart' | 'simulator' | 'vr' | 'playhouse')}
                        >
                          <option value="all">Összes típus</option>
                          <option value="gokart">Gokart</option>
                          <option value="simulator">Szimulátor</option>
                          <option value="vr">VR</option>
                          <option value="playhouse">Játszóház</option>
                        </select>
                      </div>
                      <div className="flex-1 w-full sm:w-auto">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Állapot szerint</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          onChange={(e) => setFilterStatus(e.target.value as 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled')}
                          value={filterStatus}
                        >
                          <option value="all">Összes állapot</option>
                          <option value="pending">Függőben</option>
                          <option value="confirmed">Megerősítve</option>
                          <option value="completed">Teljesítve</option>
                          <option value="cancelled">Lemondva</option>
                        </select>
                      </div>
                      {(filterDate !== null || filterType !== 'all' || filterStatus !== 'all' || filterMonth !== 'all' || filterYear !== 'all') && (
                        <div className="flex items-center">
                          <button
                            onClick={() => {
                              setFilterDate(null);
                              setFilterType('all');
                              setFilterStatus('all');
                              setFilterMonth('all');
                              setFilterYear('all');
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Szűrők törlése
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <h3 className="text-md font-medium text-blue-800 mb-2">Jövőbeli foglalások</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={handleSortByDate}
                              >
                                <div className="flex items-center">
                                  Dátum
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 ${sortOrder === 'asc' ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </div>
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Időpont</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendégek</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredBookings
                              .filter(group => {
                                const bookingDate = new Date(
                                  group.date.toDate().getFullYear(),
                                  group.date.toDate().getMonth(),
                                  group.date.toDate().getDate(),
                                  parseInt(group.startTime.split(':')[0]),
                                  parseInt(group.startTime.split(':')[1])
                                );
                                const now = new Date();
                                return bookingDate > now;  // Csak a jövőbeli foglalások
                              })
                              .map((group, groupIndex) => (
                                <tr key={`group-${groupIndex}`}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{formatDate(group.date)}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{group.startTime} - {group.endTime}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {group.bookings.some(b => b.bookingType === 'gokart') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 mr-1">
                                        Gokart
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'simulator') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 mr-1">
                                        Szimulátor
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'vr') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 mr-1">
                                        VR
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'playhouse') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1">
                                        Játszóház
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="space-y-2">
                                      {group.bookings.map((booking) => (
                                        <div key={booking.id} className="flex items-start space-x-2 pb-2 border-b border-gray-100 last:border-0">
                                          <div className={`flex-shrink-0 ${booking.bookingType === 'gokart' ? 'bg-green-100 text-green-800' :
                                            booking.bookingType === 'simulator' ? 'bg-blue-100 text-blue-800' :
                                              booking.bookingType === 'vr' ? 'bg-purple-100 text-purple-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            } px-2 py-1 rounded-full text-xs font-medium`}>
                                            {booking.bookingType === 'gokart' && `Gokart #${booking.itemNumber}`}
                                            {booking.bookingType === 'simulator' && `Szimulátor #${booking.itemNumber}`}
                                            {booking.bookingType === 'vr' && `VR #${booking.itemNumber}`}
                                            {booking.bookingType === 'playhouse' && `Játszóház #${booking.itemNumber}`}
                                          </div>
                                          <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">{booking.userName}</div>
                                            <div className="text-xs text-gray-500">{booking.userEmail}</div>
                                            {booking.racerId && (
                                              <div className="text-xs text-gray-500">ID: {booking.racerId}</div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {group.bookings.every(b => b.status === group.bookings[0].status) ? (
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${group.bookings[0].status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                          group.bookings[0].status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                            group.bookings[0].status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                              'bg-yellow-100 text-yellow-800'}`}>
                                        {group.bookings[0].status === 'confirmed' && (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {group.bookings[0].status === 'pending' && (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {group.bookings[0].status === 'completed' && (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {group.bookings[0].status === 'cancelled' && (
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                          </svg>
                                        )}
                                        {statusTranslations[group.bookings[0].status]}
                                      </span>
                                    ) : (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                        Vegyes
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex flex-col space-y-2">
                                      <button
                                        onClick={() => viewGroupDetails(group)}
                                        className="w-full text-blue-600 hover:text-blue-900 px-3 py-1 border border-blue-600 rounded flex items-center justify-center"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Részletek
                                      </button>
                                      {group.bookings.some(b => b.status === 'pending') && isMoreThan24HoursBefore(group.date) && (
                                        <button
                                          onClick={() => deleteAllBookingsInGroup(group)}
                                          className="w-full text-red-600 hover:text-red-800 px-3 py-1 border border-red-600 rounded flex items-center justify-center"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          Mind lemondása
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-md font-medium text-gray-800 mb-2">Korábbi foglalások</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dátum</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Időpont</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eszközök</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredBookings
                              .filter(group => {
                                const bookingDate = new Date(
                                  group.date.toDate().getFullYear(),
                                  group.date.toDate().getMonth(),
                                  group.date.toDate().getDate(),
                                  parseInt(group.startTime.split(':')[0]),
                                  parseInt(group.startTime.split(':')[1])
                                );
                                const now = new Date();
                                return bookingDate <= now;  // Csak a múltbeli foglalások
                              })
                              .slice(0, 5)
                              .map((group) => (
                                <tr key={`${group.date.toMillis()}_${group.startTime}_${group.bookings.map(b => b.id).join('_')}`}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(group.date)}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.startTime} - {group.endTime}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {group.bookings.some(b => b.bookingType === 'gokart') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 mr-1">
                                        Gokart
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'simulator') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 mr-1">
                                        Szimulátor
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'vr') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 mr-1">
                                        VR
                                      </span>
                                    )}
                                    {group.bookings.some(b => b.bookingType === 'playhouse') && (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1">
                                        Játszóház
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {group.bookings.length} db
                                    <span className="ml-1 text-xs text-gray-500">
                                      ({group.bookings.map(b => {
                                        switch (b.bookingType) {
                                          case 'gokart': return `G${b.itemNumber}`;
                                          case 'simulator': return `S${b.itemNumber}`;
                                          case 'vr': return `V${b.itemNumber}`;
                                          case 'playhouse': return `J${b.itemNumber}`;
                                        }
                                      }).join(', ')})
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {group.bookings.every(b => b.status === 'cancelled') ? (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                        Törölve
                                      </span>
                                    ) : group.bookings.some(b => b.status === 'completed') ? (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        Teljesítve
                                      </span>
                                    ) : (
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                        Vegyes
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex flex-col space-y-2">
                                      <button
                                        onClick={() => viewGroupDetails(group)}
                                        className="w-full text-blue-600 hover:text-blue-900 px-3 py-1 border border-blue-600 rounded flex items-center justify-center"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Részletek
                                      </button>
                                      {group.bookings.some(b => b.status === 'pending') && isMoreThan24HoursBefore(group.date) && (
                                        <button
                                          onClick={() => deleteAllBookingsInGroup(group)}
                                          className="w-full text-red-600 hover:text-red-800 px-3 py-1 border border-red-600 rounded flex items-center justify-center"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          Mind lemondása
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            {groupedBookings.filter(group => !isUpcoming(group.date)).length > 5 && (
                              <tr>
                                <td colSpan={6} className="px-6 py-4">
                                  <div className="text-right">
                                    <button
                                      onClick={() => setShowAllPastBookings(true)}
                                      className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                      Összes korábbi foglalás megtekintése
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => navigate('/public-booking')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                      >
                        Új foglalás
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="p-6 bg-white border-b border-gray-200">
              {isAdmin ? (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Admin Panel</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg shadow relative">
                      {pendingBookingsCount > 0 && (
                        <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                          {pendingBookingsCount}
                        </div>
                      )}
                      <h3 className="text-md font-medium text-blue-800 mb-2">Foglalások kezelése</h3>
                      <p className="text-sm text-blue-600">Gokart foglalások elfogadása, elutasítása és kezelése</p>
                      <div className="mt-3 flex items-center">
                        <button
                          onClick={() => navigate('/bookings')}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
                        >
                          <span>Foglalások megtekintése</span>
                          {pendingBookingsCount > 0 && (
                            <span className="ml-2 bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                              {pendingBookingsCount} függőben
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-green-800 mb-2">Felhasználók kezelése</h3>
                      <p className="text-sm text-green-600">Felhasználói fiókok és jogosultságok kezelése</p>
                      <button
                        onClick={() => navigate('/users')}
                        className="mt-3 px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                        Felhasználók megtekintése
                      </button>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-purple-800 mb-2">Gokartok kezelése</h3>
                      <p className="text-sm text-purple-600">Gokart készlet és karbantartás kezelése</p>
                      <button
                        onClick={() => navigate('/settings')}
                        className="mt-3 px-3 py-1 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700"
                      >
                        Részletes beállítások
                      </button>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-yellow-800 mb-2">Foglalási beállítások</h3>
                      <p className="text-sm text-yellow-600">Futamok hossza, időközök és nyitvatartás beállítása</p>
                      <button
                        onClick={() => navigate('/settings')}
                        className="mt-3 px-3 py-1 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700"
                      >
                        Beállítások módosítása
                      </button>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-indigo-800 mb-2">Felhasználói profil</h3>
                      <p className="text-sm text-indigo-600">Személyes adatok kezelése és frissítése</p>
                      <button
                        onClick={() => navigate('/profile')}
                        className="mt-3 px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                      >
                        Profil megtekintése
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Felhasználói vezérlőpult</h2>
                  {/* <p className="text-gray-600 mb-4">Nincs admin jogosultságod. Kérjük, vedd fel a kapcsolatot egy adminisztrátorral segítségért.</p> */}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-blue-800 mb-2">Gokart foglalás</h3>
                      <p className="text-sm text-blue-600">Foglalj időpontot gokartozásra</p>
                      <button
                        onClick={() => navigate('/public-booking')}
                        className="mt-3 px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
                      >
                        Időpont foglalása
                      </button>
                    </div>

                    <div className="bg-indigo-50 p-4 rounded-lg shadow">
                      <h3 className="text-md font-medium text-indigo-800 mb-2">Felhasználói profil</h3>
                      <p className="text-sm text-indigo-600">Személyes adatok kezelése és frissítése</p>
                      <button
                        onClick={() => navigate('/profile')}
                        className="mt-3 px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                      >
                        Profil megtekintése
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Foglalás részletek modal */}
      {showBookingDetails && selectedBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Foglalás részletei</h3>
              <button
                onClick={closeBookingDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Foglalás azonosító:</p>
                    <p className="text-sm font-medium">{selectedBooking.id.substring(0, 8)}...</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Állapot:</p>
                    <p className={`text-sm font-medium ${selectedBooking.status === 'confirmed'
                      ? 'text-green-600'
                      : selectedBooking.status === 'pending'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                      }`}>
                      {selectedBooking.status === 'confirmed'
                        ? 'Megerősítve'
                        : selectedBooking.status === 'pending'
                          ? 'Függőben'
                          : 'Törölve'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Foglalás adatai</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Dátum:</p>
                      <p className="text-sm font-medium">{formatDate(selectedBooking.date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Időpont:</p>
                      <p className="text-sm font-medium">{selectedBooking.startTime} - {selectedBooking.endTime}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Eszköz:</p>
                      <p className="text-sm font-medium">
                        {selectedBooking.bookingType === 'gokart'
                          ? `Gokart ${selectedBooking.itemNumber}`
                          : selectedBooking.bookingType === 'simulator'
                            ? `Szimulátor ${selectedBooking.itemNumber}`
                            : selectedBooking.bookingType === 'vr'
                              ? `VR ${selectedBooking.itemNumber}`
                              : `Játszóház ${selectedBooking.itemNumber}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Vendég neve:</p>
                      <p className="text-sm font-medium">{selectedBooking.userName}</p>
                    </div>
                    {selectedBooking.racerId && (
                      <div>
                        <p className="text-sm text-gray-500">Vendég azonosító:</p>
                        <p className="text-sm font-medium">{selectedBooking.racerId}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Időbélyegek</h4>
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="grid grid-cols-1 gap-2">
                    {selectedBooking.createdAt && (
                      <div>
                        <p className="text-sm text-gray-500">Létrehozva:</p>
                        <p className="text-sm font-medium">
                          {selectedBooking.createdAt.toDate().toLocaleString('hu-HU')}
                        </p>
                      </div>
                    )}
                    {selectedBooking.cancelledAt && (
                      <div>
                        <p className="text-sm text-gray-500">Lemondva:</p>
                        <p className="text-sm font-medium">
                          {selectedBooking.cancelledAt.toDate().toLocaleString('hu-HU')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                {isUpcoming(selectedBooking.date) && selectedBooking.status !== 'cancelled' && (
                  <button
                    onClick={() => {
                      closeBookingDetails();
                      deleteBooking(selectedBooking.id);
                    }}
                    className="mr-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                  >
                    Foglalás lemondása
                  </button>
                )}
                <button
                  onClick={closeBookingDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Csoport részletek modal */}
      {showGroupDetails && selectedGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
              <h3 className="text-lg font-medium text-gray-900">Foglalás részletei</h3>
              <button
                onClick={closeGroupDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Dátum:</p>
                    <p className="text-sm font-medium">{formatDate(selectedGroup.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Időpont:</p>
                    <p className="text-sm font-medium">{selectedGroup.startTime} - {selectedGroup.endTime}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Eszközök száma:</p>
                    <p className="text-sm font-medium">{selectedGroup.bookings.length} db</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Vendégek adatai</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  {selectedGroup.bookings.map((booking) => (
                    <div key={booking.id} className="border rounded-lg overflow-hidden">
                      <div className="bg-white p-2 border-b">
                        <div className="flex justify-between items-center">
                          <div className="font-medium">{booking.userName || 'Névtelen vendég'}</div>
                          <div className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                            {booking.bookingType === 'gokart'
                              ? `Gokart ${booking.itemNumber}`
                              : booking.bookingType === 'simulator'
                                ? `Szimulátor ${booking.itemNumber}`
                                : booking.bookingType === 'vr'
                                  ? `VR ${booking.itemNumber}`
                                  : `Játszóház ${booking.itemNumber}`}
                          </div>
                        </div>
                      </div>

                      <div className="p-2 space-y-1 bg-gray-50">
                        {booking.racerId && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Vendég ID:</span>
                            <span className="text-xs ml-1">
                              {booking.racerId || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.motherName || booking.motherName) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Anyja:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.motherName || booking.motherName || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.socialSecurityNumber || booking.birthDate) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Születés/TAJ:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.socialSecurityNumber || booking.birthDate || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.address || booking.address) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Cím:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.address || booking.address || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.phoneNumber || booking.phoneNumber) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Telefon:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.phoneNumber || booking.phoneNumber || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.email || booking.userEmail) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Email:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.email || booking.userEmail || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        {(booking.racerDetails?.idCardNumber || booking.idNumber) && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Szem.ig.:</span>
                            <span className="text-xs ml-1">
                              {booking.racerDetails?.idCardNumber || booking.idNumber || 'Nincs megadva'}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium text-gray-700">Állapot:</span>
                          <span className={`text-xs ml-1 font-semibold ${booking.status === 'confirmed'
                            ? 'text-green-600'
                            : booking.status === 'pending'
                              ? 'text-yellow-600'
                              : booking.status === 'completed'
                                ? 'text-blue-600'
                                : 'text-red-600'
                            }`}>
                            {booking.status === 'confirmed'
                              ? 'Megerősítve'
                              : booking.status === 'pending'
                                ? 'Függőben'
                                : booking.status === 'completed'
                                  ? 'Teljesítve'
                                  : 'Törölve'}
                          </span>
                        </div>

                        {booking.createdAt && (
                          <div className="flex items-center text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700">Létrehozva:</span>
                            <span className="text-xs ml-1">
                              {booking.createdAt.toDate().toLocaleString('hu-HU')}
                            </span>
                          </div>
                        )}
                      </div>

                      {isUpcoming(booking.date) && booking.status !== 'cancelled' && (
                        <div className="bg-white p-2 border-t flex justify-end">
                          <button
                            onClick={() => {
                              closeGroupDetails();
                              deleteBooking(booking.id);
                            }}
                            className="text-red-600 hover:text-red-800 text-xs flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Lemondás
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={closeGroupDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Bezárás
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Új modal az összes korábbi foglalás megjelenítéséhez */}
      {showAllPastBookings && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative mx-auto p-5 border w-full max-w-7xl shadow-lg rounded-md bg-white my-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Összes korábbi foglalás</h3>
              <button
                onClick={() => setShowAllPastBookings(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Szűrők */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dátum szerint</label>
                <input
                  type="date"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    setFilterDate(date);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Típus szerint</label>
                <select
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  onChange={(e) => setFilterType(e.target.value as 'all' | 'gokart' | 'simulator' | 'vr' | 'playhouse')}
                  defaultValue="all"
                >
                  <option value="all">Összes típus</option>
                  <option value="gokart">Gokart</option>
                  <option value="simulator">Szimulátor</option>
                  <option value="vr">VR</option>
                  <option value="playhouse">Játszóház</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Állapot szerint</label>
                <select
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  onChange={(e) => setFilterStatus(e.target.value as 'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled')}
                  defaultValue="all"
                >
                  <option value="all">Összes állapot</option>
                  <option value="pending">Függőben</option>
                  <option value="confirmed">Megerősítve</option>
                  <option value="completed">Teljesítve</option>
                  <option value="cancelled">Lemondva</option>
                </select>
              </div>
            </div>

            {/* Szűrők törlése gomb */}
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => {
                  setFilterDate(null);
                  setFilterType('all');
                  setFilterStatus('all');
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Szűrők törlése
              </button>
            </div>

            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700 mr-2">Elemek száma:</span>
                <select
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {/* ... existing clear filters button ... */}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={handleSortByDate}
                    >
                      <div className="flex items-center">
                        Dátum
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-4 w-4 ml-1 ${sortOrder === 'asc' ? 'transform rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Időpont</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendégek</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings
                    .filter(group => {
                      const bookingDate = group.date.toDate();
                      const now = new Date();
                      return bookingDate < now;  // Csak azokat jelenítjük meg, amik a múltban vannak
                    })
                    .slice(0, 5)
                    .map((group) => (
                      <tr key={`group-${group.date.toMillis()}_${group.startTime}_${group.bookings.map(b => b.id).join('_')}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(group.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.startTime} - {group.endTime}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {group.bookings.some(b => b.bookingType === 'gokart') && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 mr-1">
                              Gokart
                            </span>
                          )}
                          {group.bookings.some(b => b.bookingType === 'simulator') && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 mr-1">
                              Szimulátor
                            </span>
                          )}
                          {group.bookings.some(b => b.bookingType === 'vr') && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 mr-1">
                              VR
                            </span>
                          )}
                          {group.bookings.some(b => b.bookingType === 'playhouse') && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1">
                              Játszóház
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {group.bookings.length} db
                          <span className="ml-1 text-xs text-gray-500">
                            ({group.bookings.map(b => {
                              switch (b.bookingType) {
                                case 'gokart': return `G${b.itemNumber}`;
                                case 'simulator': return `S${b.itemNumber}`;
                                case 'vr': return `V${b.itemNumber}`;
                                case 'playhouse': return `J${b.itemNumber}`;
                              }
                            }).join(', ')})
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {group.bookings.every(b => b.status === 'cancelled') ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Törölve
                            </span>
                          ) : group.bookings.some(b => b.status === 'completed') ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Teljesítve
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Vegyes
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={() => viewGroupDetails(group)}
                              className="w-full text-blue-600 hover:text-blue-900 px-3 py-1 border border-blue-600 rounded flex items-center justify-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Részletek
                            </button>
                            {group.bookings.some(b => b.status === 'pending') && isMoreThan24HoursBefore(group.date) && (
                              <button
                                onClick={() => deleteAllBookingsInGroup(group)}
                                className="w-full text-red-600 hover:text-red-800 px-3 py-1 border border-red-600 rounded flex items-center justify-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Mind lemondása
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  {groupedBookings.filter(group => !isUpcoming(group.date)).length > 5 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="text-right">
                          <button
                            onClick={() => setShowAllPastBookings(true)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Összes korábbi foglalás megtekintése
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Megjelenítve: {Math.min((currentPage - 1) * itemsPerPage + 1, filteredBookings.length)} - {Math.min(currentPage * itemsPerPage, filteredBookings.length)} / {filteredBookings.length}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                >
                  Előző
                </button>
                {Array.from({ length: Math.ceil(filteredBookings.length / itemsPerPage) }).map((_, index) => {
                  const pageNumber = index + 1;
                  // Only show 5 page numbers around the current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === Math.ceil(filteredBookings.length / itemsPerPage) ||
                    (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-1 rounded-md ${currentPage === pageNumber
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border'
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 3 ||
                    pageNumber === currentPage + 3
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(filteredBookings.length / itemsPerPage)}
                  className={`px-3 py-1 rounded-md ${currentPage >= Math.ceil(filteredBookings.length / itemsPerPage)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border'
                    }`}
                >
                  Következő
                </button>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowAllPastBookings(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 